import { Box, Divider, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { ErrorCode, FileRejection, useDropzone } from 'react-dropzone';

import { BoxedIcon } from '@/components/display/Ribbon/BoxedIcon';
import { AlertTriangleIcon } from '@/components/icons/AlertTriangleIcon';
import { Upload01Icon } from '@/components/icons/Upload01Icon';
import { ProgressBar } from '@/components/progress/ProgressBar/ProgressBar';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { COLORS } from '@/styles/tokens/colors';
import { FileContext } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

import DocumentsIllustration from './DocumentsIllustration.svg?react';
import { ExistingDocumentPicker } from './ExistingDocumentPicker';
import {
  acceptedFileTypes,
  getErrorMessageFromCode,
  getFileUploadErrorMessage,
  isExpectedFileUploadError,
  isFileEncrypted,
  mapWithConcurrency,
  MAX_UPLOAD_SIZE_BYTES,
  uploadWithRetry,
} from './FileUploader.utils';
import { FileUploaderSecurityIcon } from './FileUploaderSecurityIcon';
import { getDividerStyles, getDropzoneSx } from './FileUploaderV2.styles';
import { FileUploaderV2Props } from './FileUploaderV2.types';
import { useUploadCSVV2 } from './hooks/useUploadCSVV2';
import { UploadedFileData, useUploadFile } from './hooks/useUploadFile';
import { useUploadFileV2 } from './hooks/useUploadFileV2';

// Cap simultaneous uploads so a large batch doesn't exceed browser/proxy
// connection limits (which surfaces as "Failed to fetch"). See T1-6465.
const MAX_CONCURRENT_UPLOADS = 5;

export function FileUploaderV2<Mutation>(props: FileUploaderV2Props<Mutation>) {
  const {
    sx,
    householdId,
    context = FileContext.Household,
    errorMessage: externalErrorMessage,
    selectedExistingDocumentIds,
    disabled,
    acceptTypes = acceptedFileTypes,
    showExistingDocuments = true,
    excludedExistingDocumentTypes,
    onExistingSelected,
    onUploadComplete,
    onUploadStarted,
    onUploadError,
    generateSignedUploadURL, // Only used for Logo context which still uses presigned URL flow
    allowMultiple = false,
    variant = 'compact',
    appearance = 'default',
    isCSVImport = false,
    __devForceError = false,
    __devForceUploading = false,
  } = props;

  const { reportError } = useReportError();
  const trackUserEvent = useTrackUserEvent();

  // Validate householdId requirement based on context
  const householdIdRequired = context === FileContext.Household;

  // Check for missing required householdId and log error
  const hasRequiredHouseholdIdError = householdIdRequired && !householdId;

  useEffect(() => {
    if (hasRequiredHouseholdIdError) {
      diagnostics.error('householdId is required when context is Household');
    }
  }, [hasRequiredHouseholdIdError]);

  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const errorMessage = uploadErrorMessage || externalErrorMessage;

  const [totalFiles, setTotalFiles] = useState<number>(0);
  const [singleFileUploadProgress, setSingleFileUploadProgress] =
    useState<number>(__devForceUploading ? 70 : 0);
  const [multipleFileUploadProgress, setMultipleFileUploadProgress] =
    useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(
    __devForceUploading ?? false
  );

  // Legacy presigned URL upload flow - only used for Logo context
  const { uploadFile: uploadFileLegacy } = useUploadFile({
    generateSignedUploadURL,
    setSingleFileUploadProgress,
    setMultipleFileUploadProgress,
    context,
    householdId,
  });

  // Direct file upload flow via GraphQL mutation
  const { uploadFile: uploadFileV2 } = useUploadFileV2({
    setSingleFileUploadProgress,
    setMultipleFileUploadProgress,
    context,
    householdId,
  });

  // CSV import file upload flow via GraphQL mutation
  const { uploadFile: uploadCSVFileV2 } = useUploadCSVV2({
    setSingleFileUploadProgress,
    setMultipleFileUploadProgress,
    context,
    householdId,
  });

  const uploadFile = (() => {
    // Logo uploads use the legacy presigned URL flow.
    if (context === FileContext.Logo) return uploadFileLegacy;
    if (context === FileContext.Platform) return uploadFileV2;
    if (isCSVImport) return uploadCSVFileV2;
    return uploadFileV2;
  })() as (file: File) => Promise<UploadedFileData<Mutation>>;

  // INTERNAL USE ONLY: force an error state for testing
  useEffect(() => {
    if (__devForceError) {
      setUploadErrorMessage('This is a test error');
    }
  }, [__devForceError]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (!allowMultiple && acceptedFiles.length > 1) {
        setUploadErrorMessage('Please upload only one file at a time.');
        return;
      }

      const rejectedFile = rejectedFiles[0];
      if (rejectedFile) {
        setUploadErrorMessage(getErrorMessageFromCode(rejectedFile));
        return;
      }

      // Check for password-protected PDFs and DOCX files.
      const passwordProtectedFiles: File[] = [];
      const validFiles: File[] = [];

      for (const file of acceptedFiles) {
        if (await isFileEncrypted(file)) {
          passwordProtectedFiles.push(file);
        } else {
          validFiles.push(file);
        }
      }

      // If all files are password protected, show an error message
      if (passwordProtectedFiles.length > 0 && validFiles.length === 0) {
        const passwordProtectedFilenames = passwordProtectedFiles.map(
          (file) => file.name
        );
        const joinedPasswordProtected = passwordProtectedFilenames.join(', ');
        setUploadErrorMessage(
          `Please remove the password-protected files and try again: ${joinedPasswordProtected}`
        );
        return;
      }

      try {
        setIsUploading(true);
        setTotalFiles(validFiles.length);
        setUploadErrorMessage(null);
        onUploadStarted?.(validFiles);

        const settled = await mapWithConcurrency(
          validFiles,
          MAX_CONCURRENT_UPLOADS,
          (file) => uploadWithRetry(uploadFile, file)
        );

        const successes: UploadedFileData<Mutation>[] = [];
        let firstFailure: PromiseRejectedResult | undefined;
        for (const result of settled) {
          if (result.status === 'fulfilled') {
            successes.push(result.value);
          } else if (!firstFailure) {
            firstFailure = result;
          }
        }
        const failureCount = settled.length - successes.length;

        // Always notify completion, even when every upload failed: consumers
        // guard on an empty list, and this hands back any password-protected
        // files and lets the multi-document uploader clear its uploading state.
        onUploadComplete(
          successes,
          passwordProtectedFiles.length > 0 ? passwordProtectedFiles : undefined
        );
        if (successes.length > 0 && context !== FileContext.Platform) {
          trackUserEvent('document uploaded', { count: successes.length });
        }

        if (firstFailure) {
          // For a single file, keep the specific error; for a batch, report how
          // many failed so the user can re-drop just those.
          const message =
            validFiles.length === 1
              ? getFileUploadErrorMessage(firstFailure.reason)
              : `${failureCount} of ${validFiles.length} files couldn't be uploaded. Please try again.`;
          setUploadErrorMessage(message);
          onUploadError?.(firstFailure.reason as Error);
          if (!isExpectedFileUploadError(firstFailure.reason)) {
            reportError('failed to upload file', firstFailure.reason as Error);
          }
        }
      } catch (error) {
        setUploadErrorMessage(getFileUploadErrorMessage(error));
        onUploadError?.(error as Error);
        if (!isExpectedFileUploadError(error)) {
          reportError('failed to upload file', error as Error);
        }
      } finally {
        setIsUploading(false);
        setSingleFileUploadProgress(0);
        setMultipleFileUploadProgress(0);
        setTotalFiles(0);
      }
    },
    [
      allowMultiple,
      context,
      onUploadComplete,
      onUploadError,
      onUploadStarted,
      reportError,
      trackUserEvent,
      uploadFile,
    ]
  );

  // Dropzone setup
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled: disabled || isUploading || hasRequiredHouseholdIdError,
    accept: acceptTypes,
    // Browsers can report an allowed MIME type for a disallowed filename.
    validator: (file) => {
      const extensions = Object.values(acceptTypes).flat();
      if (
        extensions.some((extension) =>
          file.name.toLowerCase().endsWith(extension.toLowerCase())
        )
      ) {
        return null;
      }
      return {
        code: ErrorCode.FileInvalidType,
        message: 'The filename must have a supported extension.',
      };
    },
    multiple: allowMultiple,
    maxSize: MAX_UPLOAD_SIZE_BYTES,
    onDrop,
  });

  const dropzoneState = (() => {
    if (disabled || hasRequiredHouseholdIdError) return 'disabled';
    if (isDragActive) return 'hover-with-file';
    if (errorMessage) return 'error';
    return 'normal';
  })();

  const dropzoneSx = { ...getDropzoneSx(dropzoneState, appearance), ...sx };
  const handleExistingSelected = (
    documents: { id: string; displayName: string }[]
  ) => {
    onExistingSelected?.(documents);
  };

  // Calculate upload progress
  const uploadProgressPercent = totalFiles
    ? Math.round((multipleFileUploadProgress / totalFiles) * 100)
    : 0;

  /**
   * Renders the upload progress UI
   */
  const renderUploadProgress = () => (
    <Stack
      spacing={1.5}
      direction="row"
      alignItems="center"
      justifyContent="center"
      width="100%"
      py={1}
    >
      <BoxedIcon icon={Upload01Icon} />
      <Stack spacing="3px" sx={{ width: '80%' }}>
        <ProgressBar
          variant="determinate"
          barVariant="solid"
          sx={{
            height: 16,
            borderRadius: 1.5,
            backgroundColor: COLORS.NAVY[700],
          }}
          value={
            totalFiles > 1 ? uploadProgressPercent : singleFileUploadProgress
          }
        />
        <Typography variant="body-xs">Uploading...</Typography>
      </Stack>
    </Stack>
  );

  /**
   * Renders the dropzone content with document illustration and upload button
   */
  const renderDropzoneContent = () => (
    <Stack
      alignItems="center"
      justifyContent="center"
      width="100%"
      spacing={2}
      py={1}
    >
      {variant === 'expanded' && (
        <Box sx={{ position: 'relative', width: 142, height: 132, mb: 2 }}>
          <DocumentsIllustration width={142} height={132} />
        </Box>
      )}

      {errorMessage ? (
        renderErrorMessage()
      ) : (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <BoxedIcon
            icon={Upload01Icon}
            iconProps={disabled ? { color: COLORS.GRAY[300] } : undefined}
            sx={disabled ? { backgroundColor: COLORS.GRAY[200] } : undefined}
          />
          <Typography
            variant="body-s"
            color={disabled ? COLORS.TEXT.DISABLED : undefined}
          >
            Drop {allowMultiple ? 'files' : 'a file'} or click to upload a new
            file
          </Typography>
        </Stack>
      )}
    </Stack>
  );

  /**
   * Renders the error message
   */
  const renderErrorMessage = () => (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <BoxedIcon
        sx={{ backgroundColor: COLORS.ORANGE[100] }}
        iconProps={{ color: COLORS.ORANGE[700] }}
        icon={AlertTriangleIcon}
      />
      <Typography variant="body-s" color={COLORS.ORANGE[900]} sx={{ mt: 2 }}>
        {errorMessage}
      </Typography>
    </Stack>
  );

  /**
   * Renders the existing documents section with divider
   */
  const renderExistingDocumentsSection = () => {
    // Only show existing documents for HOUSEHOLD context
    if (context !== FileContext.Household) {
      return null;
    }

    // Don't render if householdId is missing
    if (!householdId) {
      return null;
    }

    return (
      <Stack
        width="100%"
        spacing={2}
        alignItems="center"
        mt={variant === 'expanded' ? 3 : 0}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ width: '100%' }}
        >
          <Divider sx={getDividerStyles(dropzoneState)} />
          <Typography variant="body-xs">or</Typography>
          <Divider sx={getDividerStyles(dropzoneState)} />
        </Stack>
        <Box sx={{ width: '300px' }}>
          <ExistingDocumentPicker
            householdId={householdId!}
            // the undefined case should never happen, because renderExistingDocumentsSection
            // is only called if householdId exists (we return early if it doesn't)
            value={selectedExistingDocumentIds ?? []}
            onChange={handleExistingSelected}
            disabled={disabled || hasRequiredHouseholdIdError}
            excludedDocumentTypes={excludedExistingDocumentTypes}
          />
        </Box>
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        cursor: isUploading ? 'default' : 'pointer',
        width: '100%',
        border: '1px dashed',
        borderRadius: 1,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...dropzoneSx,
      }}
      {...getRootProps()}
    >
      {/* Lock icon in the top right; cursor: default to indicate that clicking the icon does nothing */}
      <Box sx={{ position: 'absolute', top: 8, right: 8, cursor: 'default' }}>
        <FileUploaderSecurityIcon />
      </Box>
      <input {...getInputProps()} data-testid="file-uploader" />

      {isUploading ? renderUploadProgress() : renderDropzoneContent()}

      {showExistingDocuments &&
        !isUploading &&
        renderExistingDocumentsSection()}
    </Box>
  );
}
